import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { updateBanner, deleteBanner, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import { getAdminBanner, getAllTools, getModelListUnified, getImageModelListUnified } from '../../graphql/queries';
import {
  Card,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Space,
  Row,
  Col,
  InputNumber,
  Divider,
  Switch,
  DatePicker,
  Image,
  Modal,
  Spin,
  Tag,
  Progress,
} from 'antd';
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, DeleteOutlined, HolderOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { uploadToR2 } from '../../utils/uploadToR2';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import { compressVideoToLowRes, isFFmpegSupported, getCompressionStatus } from '../../utils/videoCompression';
import TargetingRulesEditor from '../../components/TargetingRulesEditor';
import BuilderConfigEditor from '../../components/BuilderConfigEditor';
import dayjs from 'dayjs';
import './Banners.css';

const { Option } = Select;
const { TextArea } = Input;

const actionTypes = [
  { value: 'TOOL', label: 'Tool (navigate to tool)' },
  { value: 'PROMPT', label: 'Prompt (send to builder)' },
  { value: 'PAYWALL', label: 'Paywall (open paywall)' },
];

const generationModesByBuilderType = {
  IMAGE: [
    { value: 'TEXT_TO_IMAGE', label: 'Text to Image' },
    { value: 'IMAGE_TO_IMAGE', label: 'Image to Image' },
  ],
  VIDEO: [
    { value: 'TEXT_TO_VIDEO', label: 'Text to Video' },
    { value: 'IMAGE_TO_VIDEO', label: 'Image to Video' },
  ],
};

const audienceTypes = [
  { value: 'ALL', label: 'All Users' },
  { value: 'FREE_USER', label: 'Free Users' },
  { value: 'PRO_USER', label: 'Pro Users' },
  { value: 'ACTIVE_SUB_NO_CREDITS', label: 'Active Sub + No Credits' },
];

const paywallTypes = [
  { value: 'SUBSCRIPTION', label: 'Subscription' },
  { value: 'EXTRA_CREDIT', label: 'Extra Credit' },
  { value: 'FLASH_SALE', label: 'Flash Sale' },
];

// Sortable item card wrapper
const SortableItemCard = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 16,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragHandleProps: listeners })}
    </div>
  );
};

const BannerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [itemFiles, setItemFiles] = useState({});
  const [itemPreviews, setItemPreviews] = useState({});
  const [existingItemUrls, setExistingItemUrls] = useState({});
  const [compressingItems, setCompressingItems] = useState({});
  const [compressionProgress, setCompressionProgress] = useState({});
  const [collapsedItems, setCollapsedItems] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const { data, loading: queryLoading, refetch } = useQuery(getAdminBanner, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const { data: toolsData } = useQuery(getAllTools, {
    variables: { pageSize: 200, page: 0 },
  });
  const { data: videoConfigData } = useQuery(getModelListUnified);
  const { data: imageConfigData } = useQuery(getImageModelListUnified);

  const [updateBannerMutation] = useMutation(updateBanner);
  const [deleteBannerMutation] = useMutation(deleteBanner);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  useEffect(() => {
    if (data?.getAdminBanner) {
      const banner = data.getAdminBanner;
      const items = (banner.items || []).map((item) => ({
        actionType: item.actionType,
        toolId: item.toolId,
        promptText: item.promptText,
        builderType: item.builderType,
        generationMode: item.generationMode,
        builderConfig: item.builderConfig || null,
        paywallType: item.paywallType,
        paywallId: item.paywallId,
        audienceType: item.audienceType,
        targetingRules: item.targetingRules || null,
        sortOrder: item.sortOrder,
        isActive: item.isActive,
      }));

      form.setFieldsValue({
        name: banner.name,
        rowNumber: banner.rowNumber,
        isActive: banner.isActive,
        audienceType: banner.audienceType,
        targetingRules: banner.targetingRules || null,
        startDate: banner.startDate ? dayjs(banner.startDate) : null,
        endDate: banner.endDate ? dayjs(banner.endDate) : null,
        items,
      });

      const previews = {};
      const urls = {};
      banner.items?.forEach((item, i) => {
        previews[i] = {};
        urls[i] = {};
        if (item.imageUrl) {
          previews[i].image = item.imageUrl;
          urls[i].imageUrl = item.imageUrl;
        }
        if (item.videoUrl) {
          previews[i].video = item.videoUrl;
          urls[i].videoUrl = item.videoUrl;
        }
        if (item.lowResolutionUrl) {
          previews[i].lowResVideo = item.lowResolutionUrl;
          urls[i].lowResolutionUrl = item.lowResolutionUrl;
        }
      });
      setItemPreviews(previews);
      setExistingItemUrls(urls);
      setFormReady(true);
    }
  }, [data, form]);

  const handleItemFileClear = (itemIndex, type) => {
    setItemFiles((prev) => {
      const updated = { ...prev };
      if (updated[itemIndex]) {
        updated[itemIndex] = { ...updated[itemIndex] };
        delete updated[itemIndex][type];
      }
      return updated;
    });
    setItemPreviews((prev) => ({
      ...prev,
      [itemIndex]: { ...prev[itemIndex], [type]: undefined },
    }));
    const urlKey = type === 'image' ? 'imageUrl' : type === 'video' ? 'videoUrl' : 'lowResolutionUrl';
    setExistingItemUrls((prev) => ({
      ...prev,
      [itemIndex]: {
        ...prev[itemIndex],
        [urlKey]: null,
        ...(type === 'video' ? { lowResolutionUrl: null } : {}),
      },
    }));
    if (type === 'video') {
      setItemFiles((prev) => {
        const updated = { ...prev };
        if (updated[itemIndex]) {
          updated[itemIndex] = { ...updated[itemIndex] };
          delete updated[itemIndex].lowResVideo;
        }
        return updated;
      });
      setItemPreviews((prev) => ({
        ...prev,
        [itemIndex]: { ...prev[itemIndex], lowResVideo: undefined },
      }));
    }
  };

  const handleItemFileChange = async (itemIndex, type, file) => {
    if (file) {
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        showSnackBar('error', 'File Too Large', 'File must be smaller than 100MB');
        return false;
      }

      setItemFiles((prev) => ({
        ...prev,
        [itemIndex]: { ...prev[itemIndex], [type]: file },
      }));
      setItemPreviews((prev) => ({
        ...prev,
        [itemIndex]: { ...prev[itemIndex], [type]: URL.createObjectURL(file) },
      }));
      showSnackBar('success', 'File Selected', `New ${type} file selected`);

      // Auto-generate low resolution video when video is uploaded
      if (type === 'video' && isFFmpegSupported()) {
        try {
          setCompressingItems((prev) => ({ ...prev, [itemIndex]: true }));
          setCompressionProgress((prev) => ({ ...prev, [itemIndex]: 0 }));
          showSnackBar('info', 'Compressing Video', `Generating low resolution version for item ${itemIndex + 1}...`);

          const lowResFile = await compressVideoToLowRes(file, (progress) => {
            setCompressionProgress((prev) => ({ ...prev, [itemIndex]: progress }));
          });

          setItemFiles((prev) => ({
            ...prev,
            [itemIndex]: { ...prev[itemIndex], lowResVideo: lowResFile },
          }));
          setItemPreviews((prev) => ({
            ...prev,
            [itemIndex]: { ...prev[itemIndex], lowResVideo: URL.createObjectURL(lowResFile) },
          }));
          // Clear existing low-res URL since we have a new one
          setExistingItemUrls((prev) => ({
            ...prev,
            [itemIndex]: { ...prev[itemIndex], lowResolutionUrl: undefined },
          }));
          showSnackBar('success', 'Compression Complete', `Low resolution video generated for item ${itemIndex + 1}`);
        } catch (error) {
          console.error('Failed to compress video:', error);
          showSnackBar('warning', 'Compression Failed', 'Could not generate low resolution video. You can still proceed.');
        } finally {
          setCompressingItems((prev) => ({ ...prev, [itemIndex]: false }));
          setCompressionProgress((prev) => ({ ...prev, [itemIndex]: 0 }));
        }
      }
    }
    return false;
  };

  const uploadFile = async (file) => {
    const { data: signedData } = await getSignedUrlMutation({
      variables: { fileName: file.name },
    });
    const urlData = signedData.getSignedUrl;
    await uploadToR2(file, urlData.url, urlData.contentType);
    const { data: objData } = await getObjectUrlMutation({
      variables: { key: urlData.id, eTag: 'etag' },
    });
    return objData.getObjectUrl;
  };

  const removeFromMap = (map, removedIndex) => {
    const result = {};
    Object.keys(map).forEach((key) => {
      const k = Number(key);
      if (k < removedIndex) result[k] = map[k];
      else if (k > removedIndex) result[k - 1] = map[k];
    });
    return result;
  };

  const reindexMap = (map, fromIndex, toIndex) => {
    const items = form.getFieldValue('items') || [];
    const ordered = [];
    for (let i = 0; i < items.length; i++) {
      ordered.push(map[i] || undefined);
    }
    const item = ordered.splice(fromIndex, 1)[0];
    ordered.splice(toIndex, 0, item);
    const result = {};
    ordered.forEach((val, i) => {
      if (val) result[i] = val;
    });
    return result;
  };

  const handleDragEnd = ({ active, over }, move, fields) => {
    if (!over || active.id === over.id) return;

    const fromIndex = fields.findIndex((f) => f.key === active.id);
    const toIndex = fields.findIndex((f) => f.key === over.id);

    if (fromIndex === -1 || toIndex === -1) return;

    move(fromIndex, toIndex);
    setItemFiles((prev) => reindexMap(prev, fromIndex, toIndex));
    setItemPreviews((prev) => reindexMap(prev, fromIndex, toIndex));
    setExistingItemUrls((prev) => reindexMap(prev, fromIndex, toIndex));
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const itemsWithUrls = [];
      const formItems = values.items || [];

      for (let i = 0; i < formItems.length; i++) {
        const item = formItems[i];
        const files = itemFiles[i] || {};
        const existing = existingItemUrls[i] || {};

        let imageUrl = existing.imageUrl || null;
        let videoUrl = existing.videoUrl || null;
        let lowResolutionUrl = existing.lowResolutionUrl || null;

        if (files.image) {
          showSnackBar('info', 'Uploading', `Uploading image for item ${i + 1}...`);
          imageUrl = await uploadFile(files.image);
        }
        if (files.video) {
          showSnackBar('info', 'Uploading', `Uploading video for item ${i + 1}...`);
          videoUrl = await uploadFile(files.video);
        }
        if (files.lowResVideo) {
          showSnackBar('info', 'Uploading', `Uploading low-res video for item ${i + 1}...`);
          lowResolutionUrl = await uploadFile(files.lowResVideo);
        }

        if (!imageUrl && !videoUrl) {
          showSnackBar('error', 'Missing Media', `Item ${i + 1} must have at least an image or video`);
          setLoading(false);
          return;
        }

        itemsWithUrls.push({
          imageUrl,
          videoUrl,
          lowResolutionUrl,
          actionType: item.actionType,
          toolId: item.actionType === 'TOOL' ? item.toolId : null,
          promptText: item.actionType === 'PROMPT' ? item.promptText : null,
          builderType: item.actionType === 'PROMPT' ? item.builderType : null,
          generationMode: item.actionType === 'PROMPT' ? item.generationMode : null,
          builderConfig: item.actionType === 'PROMPT' ? item.builderConfig || null : null,
          paywallType: item.actionType === 'PAYWALL' ? item.paywallType : null,
          paywallId: item.actionType === 'PAYWALL' ? item.paywallId : null,
          audienceType: item.audienceType || null,
          targetingRules: item.targetingRules || null,
          sortOrder: i,
          isActive: item.isActive !== undefined ? item.isActive : true,
        });
      }

      const input = {
        name: values.name,
        rowNumber: values.rowNumber,
        isActive: values.isActive,
        audienceType: values.audienceType,
        targetingRules: values.targetingRules || null,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
        items: itemsWithUrls,
      };

      await updateBannerMutation({ variables: { id, input } });

      showSnackBar('success', 'Success', 'Banner group updated successfully');
      await refetch();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update banner');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Banner Group',
      content: 'Are you sure you want to delete this banner group? This will also delete all items.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteBannerMutation({ variables: { id } });
          showSnackBar('success', 'Success', 'Banner group deleted');
          navigate('/banners');
        } catch (error) {
          showSnackBar('error', 'Error', error.message || 'Failed to delete banner');
        }
      },
    });
  };

  const renderItemCard = (name, restField) => (
    <Row gutter={16}>
      <Col span={24}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {/* Image Card */}
          <div style={{
            flex: '1 1 160px',
            maxWidth: 200,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 12,
            background: '#fafafa',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Image
              {existingItemUrls[name]?.imageUrl && !itemFiles[name]?.image && (
                <Tag color="blue" style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>Saved</Tag>
              )}
            </div>
            {itemPreviews[name]?.image ? (
              <div style={{ marginBottom: 8 }}>
                <Image
                  src={itemPreviews[name].image}
                  width="100%"
                  height={100}
                  style={{ objectFit: 'cover', borderRadius: 6 }}
                />
              </div>
            ) : (
              <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8e8e8', borderRadius: 6, marginBottom: 8, color: '#aaa', fontSize: 12 }}>
                No image
              </div>
            )}
            <Upload beforeUpload={(file) => handleItemFileChange(name, 'image', file)} accept="image/*" showUploadList={false}>
              <Button icon={<UploadOutlined />} size="small" block>
                {itemPreviews[name]?.image ? 'Change' : 'Select'}
              </Button>
            </Upload>
            {itemPreviews[name]?.image && (
              <Button
                danger
                size="small"
                block
                style={{ marginTop: 4 }}
                onClick={() => handleItemFileClear(name, 'image')}
              >
                Remove
              </Button>
            )}
          </div>

          {/* Video Card */}
          <div style={{
            flex: '1 1 160px',
            maxWidth: 200,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 12,
            background: '#fafafa',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Video
              {existingItemUrls[name]?.videoUrl && !itemFiles[name]?.video && (
                <Tag color="blue" style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>Saved</Tag>
              )}
            </div>
            {itemPreviews[name]?.video ? (
              <div style={{ marginBottom: 8 }}>
                <video src={itemPreviews[name].video} width="100%" height={100} style={{ objectFit: 'cover', borderRadius: 6 }} controls />
              </div>
            ) : (
              <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8e8e8', borderRadius: 6, marginBottom: 8, color: '#aaa', fontSize: 12 }}>
                No video
              </div>
            )}
            {compressingItems[name] ? (
              <Progress percent={compressionProgress[name] || 0} size="small" status="active" format={(p) => getCompressionStatus(p)} />
            ) : (
              <Upload beforeUpload={(file) => handleItemFileChange(name, 'video', file)} accept="video/*" showUploadList={false}>
                <Button icon={<UploadOutlined />} size="small" block>
                  {itemPreviews[name]?.video ? 'Change' : 'Select'}
                </Button>
              </Upload>
            )}
            {!compressingItems[name] && itemPreviews[name]?.video && (
              <Button
                danger
                size="small"
                block
                style={{ marginTop: 4 }}
                onClick={() => handleItemFileClear(name, 'video')}
              >
                Remove
              </Button>
            )}
          </div>

          {/* Low Res Video Card */}
          <div style={{
            flex: '1 1 160px',
            maxWidth: 200,
            border: itemPreviews[name]?.lowResVideo ? '1px solid #b7eb8f' : '1px dashed #d9d9d9',
            borderRadius: 8,
            padding: 12,
            background: itemPreviews[name]?.lowResVideo ? '#f6ffed' : '#fafafa',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Low Res{' '}
              {itemPreviews[name]?.lowResVideo && (
                existingItemUrls[name]?.lowResolutionUrl && !itemFiles[name]?.lowResVideo
                  ? <Tag color="blue" style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>Saved</Tag>
                  : <Tag color="green" style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>Auto</Tag>
              )}
            </div>
            {itemPreviews[name]?.lowResVideo ? (
              <div style={{ marginBottom: 8 }}>
                <video src={itemPreviews[name].lowResVideo} width="100%" height={100} style={{ objectFit: 'cover', borderRadius: 6 }} controls />
              </div>
            ) : (
              <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8e8e8', borderRadius: 6, marginBottom: 8, color: '#aaa', fontSize: 11 }}>
                Auto-generated
              </div>
            )}
            {itemFiles[name]?.lowResVideo && (
              <div style={{ fontSize: 11, color: '#888' }}>
                {(itemFiles[name].lowResVideo.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}
          </div>
        </div>
      </Col>

      <Col span={24}>
        <Form.Item
          {...restField}
          name={[name, 'actionType']}
          label="Action Type"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Select
            placeholder="Select action"
            onChange={() => form.setFields([
              { name: ['items', name, 'toolId'], value: undefined },
              { name: ['items', name, 'promptText'], value: undefined },
              { name: ['items', name, 'builderType'], value: undefined },
              { name: ['items', name, 'generationMode'], value: undefined },
              { name: ['items', name, 'builderConfig'], value: undefined },
              { name: ['items', name, 'paywallType'], value: undefined },
              { name: ['items', name, 'paywallId'], value: undefined },
            ])}
          >
            {actionTypes.map((t) => (
              <Option key={t.value} value={t.value}>{t.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => {
          return prev.items?.[name]?.actionType !== cur.items?.[name]?.actionType ||
            prev.items?.[name]?.builderType !== cur.items?.[name]?.builderType ||
            prev.items?.[name]?.generationMode !== cur.items?.[name]?.generationMode;
        }}>
          {() => {
            const currentAction = form.getFieldValue(['items', name, 'actionType']);

            if (currentAction === 'TOOL') {
              return (
                <Form.Item
                  {...restField}
                  name={[name, 'toolId']}
                  label="Tool"
                  rules={[{ required: true, message: 'Please select a tool' }]}
                >
                  <Select placeholder="Select tool" showSearch optionFilterProp="children">
                    {toolsData?.getAllTools?.tools?.map((tool) => (
                      <Option key={tool.id} value={tool.id}>{tool.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            }

            if (currentAction === 'PROMPT') {
              const currentBuilderType = form.getFieldValue(['items', name, 'builderType']);
              const currentGenerationMode = form.getFieldValue(['items', name, 'generationMode']);
              const modelsForMode = currentBuilderType === 'VIDEO'
                ? (videoConfigData?.getModelListUnified?.models || [])
                : (imageConfigData?.getImageModelListUnified?.models || []);
              return (
                <>
                  <Form.Item
                    {...restField}
                    name={[name, 'builderType']}
                    label="Builder Type"
                    rules={[{ required: true, message: 'Please select builder type' }]}
                  >
                    <Select
                      placeholder="Select builder"
                      onChange={() => form.setFields([
                        { name: ['items', name, 'generationMode'], value: undefined },
                        { name: ['items', name, 'builderConfig'], value: undefined },
                      ])}
                    >
                      <Option value="IMAGE">Image Generation</Option>
                      <Option value="VIDEO">Video Generation</Option>
                    </Select>
                  </Form.Item>
                  {currentBuilderType && (
                    <Form.Item
                      {...restField}
                      name={[name, 'generationMode']}
                      label="Generation Mode"
                      rules={[{ required: true, message: 'Please select generation mode' }]}
                    >
                      <Select
                        placeholder="Select generation mode"
                        onChange={() => form.setFieldValue(['items', name, 'builderConfig'], undefined)}
                      >
                        {generationModesByBuilderType[currentBuilderType].map((m) => (
                          <Option key={m.value} value={m.value}>{m.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {currentBuilderType && currentGenerationMode && (
                    <Form.Item
                      {...restField}
                      name={[name, 'builderConfig']}
                      label="Builder Config"
                    >
                      <BuilderConfigEditor
                        builderType={currentBuilderType}
                        videoModels={currentBuilderType === 'VIDEO' ? modelsForMode : []}
                        imageModels={currentBuilderType === 'IMAGE' ? modelsForMode : []}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    {...restField}
                    name={[name, 'promptText']}
                    label="Prompt Text"
                  >
                    <TextArea rows={3} placeholder="Enter prompt text" />
                  </Form.Item>
                </>
              );
            }

            if (currentAction === 'PAYWALL') {
              return (
                <>
                  <Form.Item
                    {...restField}
                    name={[name, 'paywallType']}
                    label="Paywall Type"
                    rules={[{ required: true, message: 'Please select paywall type' }]}
                  >
                    <Select placeholder="Select paywall type">
                      {paywallTypes.map((t) => (
                        <Option key={t.value} value={t.value}>{t.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'paywallId']}
                    label="RevenueCat Paywall ID"
                    rules={[{ required: true, message: 'Please enter paywall ID' }]}
                  >
                    <Input placeholder="e.g. winbackoffer, spring_sale" />
                  </Form.Item>
                </>
              );
            }

            return null;
          }}
        </Form.Item>

        <Form.Item {...restField} name={[name, 'targetingRules']} label="Item Targeting Override">
          <TargetingRulesEditor />
        </Form.Item>

        <Form.Item {...restField} name={[name, 'isActive']} label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Col>
    </Row>
  );

  if (queryLoading || !formReady) {
    return (
      <div className="banners-container" style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="banners-container">
      <Card>
        <div className="table-header">
          <h2>Edit Banner Group</h2>
          <Space>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
            <Button onClick={() => navigate('/banners')}>Back to List</Button>
          </Space>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Group Name"
                rules={[{ required: true, message: 'Please enter group name' }]}
              >
                <Input placeholder="e.g. Spring Promotion, Tool Showcase" />
              </Form.Item>

              <Form.Item name="rowNumber" label="Row Number (display order)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="targetingRules" label="Targeting Rules">
                <TargetingRulesEditor />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              <Form.Item name="startDate" label="Start Date (optional)">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="endDate" label="End Date (optional)">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Banner Items (drag to reorder)</Divider>

          <Form.List name="items">
            {(fields, { add, remove, move }) => (
              <div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event, move, fields)}
                >
                  <SortableContext
                    items={fields.map((f) => f.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {fields.map(({ key, name, ...restField }) => (
                      <SortableItemCard key={key} id={key}>
                        {({ dragHandleProps }) => (
                          <Card
                            size="small"
                            title={
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <HolderOutlined
                                  style={{ cursor: 'grab', color: '#999' }}
                                  {...dragHandleProps}
                                />
                                <span
                                  style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                                  onClick={() => setCollapsedItems((prev) => ({ ...prev, [key]: !prev[key] }))}
                                >
                                  {collapsedItems[key] ? <RightOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
                                  Item {name + 1}
                                  {collapsedItems[key] && itemPreviews[name]?.image && (
                                    <img src={itemPreviews[name].image} alt="" style={{ height: 24, width: 42, objectFit: 'cover', borderRadius: 3, marginLeft: 4 }} />
                                  )}
                                </span>
                              </span>
                            }
                            extra={
                              fields.length > 1 ? (
                                <Button
                                  type="text"
                                  danger
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => {
                                    remove(name);
                                    setItemFiles((prev) => removeFromMap(prev, name));
                                    setItemPreviews((prev) => removeFromMap(prev, name));
                                    setExistingItemUrls((prev) => removeFromMap(prev, name));
                                    setCompressingItems((prev) => removeFromMap(prev, name));
                                    setCompressionProgress((prev) => removeFromMap(prev, name));
                                  }}
                                >
                                  Remove
                                </Button>
                              ) : null
                            }
                            bodyStyle={collapsedItems[key] ? { display: 'none' } : {}}
                          >
                            {renderItemCard(name, restField)}
                          </Card>
                        )}
                      </SortableItemCard>
                    ))}
                  </SortableContext>
                </DndContext>

                <Button
                  type="dashed"
                  onClick={() => add({ actionType: 'TOOL', isActive: true })}
                  icon={<PlusOutlined />}
                  style={{ width: '100%', marginBottom: 24 }}
                >
                  Add Item
                </Button>
              </div>
            )}
          </Form.List>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Banner Group
              </Button>
              <Button onClick={() => navigate('/banners')}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default BannerDetail;
